import useDropdown from 'hooks/useDropdown';
import React from 'react';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { ReactComponent as ChevronIcon } from 'images/chevron.svg';
import clsx from 'clsx';
import { Debt, LoanType, LoanTypeHumanized, Tradeline } from 'handlers/applicationData';
import { ReactComponent as PersonalLoanIcon } from 'images/fc-personal-loan.svg';
import { ReactComponent as CarLoanIcon } from 'images/fc-car-loan.svg';
import { ReactComponent as CreditCardIcon } from 'images/fc-credit-card.svg';
import { ReactComponent as MortgageIcon } from 'images/fc-mortgage.svg';
import { ReactComponent as StudentLoanIcon } from 'images/fc-student-loan.svg';

import { getTotalAmountForCategory } from 'components/FinancialCheckup/YourFinances/yourFinancesUtils';
import ProgressBar, { ProgressBarVariant } from 'components/ProgressBar';

import styles from './DebtRow.module.scss';

const getDefaultIcon = (loanType: string) => {
  const loanTypeLogos = {
    [LoanType.PersonalLoan]: <PersonalLoanIcon height="16px" />,
    [LoanType.AutoLoan]: <CarLoanIcon height="16px" />,
    [LoanType.CreditCard]: <CreditCardIcon height="16px" />,
    [LoanType.Mortgage]: <MortgageIcon height="16px" />,
    [LoanType.StudentLoan]: <StudentLoanIcon height="16px" />,
  };

  return loanTypeLogos[loanType as keyof typeof loanTypeLogos];
};

const getTradelineVisual = (tradeline: Tradeline) => {
  if (tradeline.logoUrl) {
    return <img src={tradeline.logoUrl} alt={`${tradeline.firm} logo`} className={styles.logoImg} />;
  }

  return getDefaultIcon(tradeline.loanType);
};

const DebtRow = ({ debt, totalBalance, index }: { debt: Debt; totalBalance: number; index: number }) => {
  const { isOpen, handleToggleDropdown, contentRef, height } = useDropdown();

  const progressPercentage = (getTotalAmountForCategory(debt.tradelines) / totalBalance) * 100;

  return (
    <div className={styles.row} data-debt-row-index={index}>
      <div className={styles.rowHeader} onClick={handleToggleDropdown}>
        <div className={styles.rowHeaderLeft}>
          <div className={styles.rowText}>
            <p className={styles.rowTitle}>{LoanTypeHumanized[debt.name as keyof typeof LoanTypeHumanized]}</p>
            <div className={styles.rowHeaderRight}>
              <p className={styles.rowApr}>{debt.apr}% APR**</p>
              <p className={styles.rowBalance}>{formatMonetaryAmount(getTotalAmountForCategory(debt.tradelines))}</p>
            </div>
          </div>
          <ProgressBar progress={progressPercentage} variant={ProgressBarVariant.Slim} />
        </div>
        <div>
          <ChevronIcon className={clsx(styles.dropdownIcon, { [styles.expanded]: isOpen })} />
        </div>
      </div>
      <div ref={contentRef} style={{ height }} className={styles.dropdownContent}>
        {debt.tradelines.map((tradeline, idx) => (
          <div className={styles.accountRow} key={`${tradeline.firm.trim()}-${idx}`}>
            <div className={styles.accountRowText}>
              <div className={styles.accountLogo}>{getTradelineVisual(tradeline)}</div>
              <p className={styles.accountName}>{tradeline.firm}</p>
            </div>
            <p className={styles.accountAmount}>{formatMonetaryAmount(tradeline.balance)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DebtRow;
